/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { CardCategory } from '@coursera/cds-core';
import type { ProductCard as ProductCardTrackingData, Recommender } from '@coursera/event-pulse-types';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardBadge from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import type { ImageParams } from 'bundles/product-card/components/LearningProductCardImage';
import ProductCardCourseraPlus from 'bundles/product-card/components/legacy/ProductCardCourseraPlus';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';

const LAZY_LOAD_THRESHOLD = 5;

type Props = {
  id?: string;
  name?: string;
  slug?: string;
  objectUrl?: string;
  imageUrl?: string;
  imageProps?: ImageParams;
  productType?: (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];
  productDifficultyLevel?: string;
  productDuration?: string;
  partners?: { id: string; logo?: string; name: string }[];
  partnerLogos?: string[];
  statusTags?: string[] | null;
  coBrandingEnabled?: boolean;
  skills?: string[];
  avgProductRatings?: number;
  numProductRatings?: number;
  isNewContent?: boolean;
  isPartOfCourseraPlus?: boolean;
  ownsCourseraPlus?: boolean;
  ownsCourseraLite?: boolean;
  cardPosition?: number;
  shouldPrioritizeImageLoading?: boolean;
  forceOpenInNewTab?: boolean;
  variant?: 'list' | 'grid';
  recommender?: Recommender;
  indexName?: string;
  trackedLinkProps?: {
    trackingName: string;
    withVisibilityTracking: boolean;
    requireFullyVisible: boolean;
    data: Record<string, unknown>;
    refAlt?: React.RefObject<HTMLAnchorElement>;
  };
  additionalProductCardTrackingData?: Partial<ProductCardTrackingData>;
  searchedQuery?: string;
  isPathwayContent?: boolean;
};

const SearchCard = ({
  id,
  name,
  imageUrl,
  imageProps,
  objectUrl,
  slug,
  productType,
  productDifficultyLevel,
  productDuration,
  partners,
  partnerLogos,
  coBrandingEnabled = false,
  skills,
  avgProductRatings,
  numProductRatings,
  isNewContent,
  isPartOfCourseraPlus,
  ownsCourseraLite,
  ownsCourseraPlus,
  statusTags,
  cardPosition,
  shouldPrioritizeImageLoading,
  forceOpenInNewTab,
  variant,
  recommender,
  indexName,
  trackedLinkProps,
  additionalProductCardTrackingData,
  searchedQuery,
  isPathwayContent,
}: Props) => {
  return (
    <LearningProductCard
      ImageComponent={
        <LearningProductCardImage
          imageUrl={imageUrl || ''}
          enableFluidWidthForLazyImg={true}
          prioritizeImageLoading={shouldPrioritizeImageLoading}
          enableLazyLoading={cardPosition ? cardPosition > LAZY_LOAD_THRESHOLD : true}
          imageParams={{ ...imageProps, ...getImagePropOverrides(partners ?? [], imageUrl ?? '') }}
        />
      }
      linkProps={{
        href: objectUrl,
        target: forceOpenInNewTab ? '_blank' : undefined,
        component: TrackedLink2,
      }}
      badge={
        <>
          <CardCategory>
            <ProductCardCourseraPlus
              isPartOfCourseraPlus={isPartOfCourseraPlus ?? false}
              ownsCourseraLite={ownsCourseraLite ?? false}
              ownsCourseraPlus={ownsCourseraPlus ?? false}
              productType={productType}
            />
          </CardCategory>
          <LearningProductCardBadge
            customBadge={isPathwayContent ? <PathwayContentBadge /> : undefined}
            productType={productType}
          />
        </>
      }
      partners={partners && getPartnerNamesAndLogos(partners, coBrandingEnabled, partnerLogos)}
      skills={skills}
      numProductRatings={numProductRatings}
      avgProductRating={avgProductRatings}
      productType={productType}
      productDifficultyLevel={productDifficultyLevel}
      productDuration={productDuration}
      statusTags={getProductCardStatusTags({ isNew: isNewContent, statusTags, slug })}
      id={id}
      name={name || ''}
      slug={slug}
      variant={variant}
      cardIndex={cardPosition}
      indexName={indexName}
      recommender={recommender}
      trackingProps={{
        trackedLinkProps,
        additionalV3TrackingData: { searchedQuery },
        additionalProductCardTrackingData,
      }}
    />
  );
};

export default SearchCard;
